import { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faGripVertical,
  faExpand,
  faCompress,
  faExternalLinkAlt,
  faWindowMinimize,
} from '@/fontawesome';
import { useDraggable } from '@/hooks/useDraggable';
import { checkVideoLiveStatus } from '@/services/youtube.api';
import { extractYouTubeVideoId } from '@/utils/youtube-url';

interface LivestreamPiPProps {
  youtubeUrl: string;
  minimized: boolean;
  onMinimize: () => void;
}

const DEFAULT_POSITION = { right: 16, bottom: 16 };
const VIDEO_WIDTH_SMALL = 384;
const IFRAME_API_SRC = 'https://www.youtube.com/iframe_api';

// Module-level promise ensures the IFrame API script is loaded exactly once,
// even if multiple LivestreamPiP instances mount concurrently.
let iframeApiPromise: Promise<YTNamespace> | null = null;

function loadYouTubeIframeAPI(): Promise<YTNamespace> {
  if (iframeApiPromise !== null) {
    return iframeApiPromise;
  }

  iframeApiPromise = new Promise<YTNamespace>((resolve) => {
    if (window.YT !== undefined && window.YT.Player !== undefined) {
      resolve(window.YT);
      return;
    }

    const prevCallback = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (prevCallback !== undefined) {
        prevCallback();
      }
      if (window.YT !== undefined) {
        resolve(window.YT);
      }
    };

    const existing = document.querySelector(`script[src="${IFRAME_API_SRC}"]`);
    if (existing === null) {
      const script = document.createElement('script');
      script.src = IFRAME_API_SRC;
      script.async = true;
      document.head.appendChild(script);
    }
  });

  return iframeApiPromise;
}

export default function LivestreamPiP({ youtubeUrl, minimized, onMinimize }: LivestreamPiPProps) {
  const [mode, setMode] = useState<'small' | 'expanded'>('small');

  const handleRef = useRef<HTMLDivElement>(null);
  const elementRef = useRef<HTMLDivElement>(null);
  const playerTargetRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<YTPlayer | null>(null);
  const errorRecoveryTimerRef = useRef<number | null>(null);
  const liveStatusFetchInFlightRef = useRef(false);

  const videoId = extractYouTubeVideoId(youtubeUrl);
  const isActive = !minimized && videoId !== null;

  const { position, setPosition, isDragging } = useDraggable({
    initialPosition: DEFAULT_POSITION,
    handleRef,
    elementRef,
    enabled: isActive,
  });

  // Create / destroy the YT.Player when videoId or visibility changes.
  useEffect(() => {
    if (videoId === null || minimized) {
      return;
    }
    const target = playerTargetRef.current;
    if (target === null) {
      return;
    }

    let cancelled = false;
    // Tracks whether the player below has been destroyed, so async
    // callbacks (API fetches, timers) know to bail out instead of
    // operating on a destroyed player instance.
    let destroyed = false;

    loadYouTubeIframeAPI().then((YT) => {
      if (cancelled) {
        return;
      }
      // If the target has been re-created (parent re-render), bail.
      if (playerTargetRef.current !== target) {
        return;
      }

      console.log('[LivestreamPIP] Creating YT.Player for videoId', videoId);

      const player = new YT.Player(target, {
        videoId,
        playerVars: {
          autoplay: 1,
          disablekb: 1,
          fs: 0,
          iv_load_policy: 3,
          rel: 0,
          modestbranding: 1,
          playsinline: 1,
          enablejsapi: 1,
          origin: window.location.origin,
        },
        events: {
          onStateChange: (event) => {
            console.log('[LivestreamPIP] YT.PlayerState', event.data);
            if (event.data !== YT.PlayerState.PAUSED && event.data !== YT.PlayerState.ENDED) {
              return;
            }
            // Only auto-resume/reload if the video is actually a live
            // broadcast right now. We fetch this on-demand (not on a
            // timer) so there is no polling load and no stale state —
            // one API call per pause event, and only while the PiP is
            // open.
            if (liveStatusFetchInFlightRef.current) {
              return;
            }
            liveStatusFetchInFlightRef.current = true;
            checkVideoLiveStatus(videoId).then((result) => {
              console.log('[LivestreamPIP] Live status for videoId', videoId, ':', result);

              liveStatusFetchInFlightRef.current = false;
              if (destroyed || result.status !== 'live') {
                return;
              }
              // Re-check the player state — the user might have hit play
              // again while we were waiting for the API.
              const currentState = event.target.getPlayerState();
              if (currentState === YT.PlayerState.PAUSED) {
                console.log('[LivestreamPIP] Auto-resuming paused livestream');
                event.target.playVideo();
              } else if (currentState === YT.PlayerState.ENDED) {
                console.log('[LivestreamPIP] Reloading livestream after ended state');
                event.target.loadVideoById(videoId);
              }
            });
          },
          onError: (event) => {
            console.log('[LivestreamPIP] YT.PlayerError', event.data);
            if (errorRecoveryTimerRef.current !== null) {
              return;
            }
            errorRecoveryTimerRef.current = window.setTimeout(() => {
              errorRecoveryTimerRef.current = null;
              if (destroyed) {
                return;
              }
              checkVideoLiveStatus(videoId).then((result) => {
                if (destroyed || result.status !== 'live') {
                  return;
                }
                event.target.loadVideoById(videoId);
              });
            }, 2000);
          },
        },
      });

      playerRef.current = player;
    });

    return () => {
      console.log('[LivestreamPIP] Cleaning up YT.Player for videoId', videoId);

      cancelled = true;
      destroyed = true;
      if (errorRecoveryTimerRef.current !== null) {
        window.clearTimeout(errorRecoveryTimerRef.current);
        errorRecoveryTimerRef.current = null;
      }
      if (playerRef.current !== null) {
        playerRef.current.destroy();
        playerRef.current = null;
      }
    };
  }, [videoId, minimized]);

  if (minimized || videoId === null) {
    return null;
  }

  const handleExpand = () => {
    setMode((prev) => (prev === 'small' ? 'expanded' : 'small'));
    setPosition(DEFAULT_POSITION);
  };

  const handlePopOut = () => {
    window.open(youtubeUrl, 'livestream', 'width=800,height=600,menubar=no,toolbar=no');
    onMinimize();
  };

  const isExpanded = mode === 'expanded';

  const containerStyle: React.CSSProperties = {
    position: 'fixed',
    right: position.right,
    bottom: position.bottom,
    zIndex: 40,
  };

  if (isExpanded) {
    containerStyle.height = 'min(50vh, calc(100vh - 32px))';
    containerStyle.width = `calc((min(50vh, calc(100vh - 32px)) - 36px) * 16 / 9)`;
  } else {
    containerStyle.width = VIDEO_WIDTH_SMALL;
  }

  return (
    <div ref={elementRef} style={containerStyle} className="rounded-lg shadow-2xl overflow-hidden flex flex-col border border-gray-200 dark:border-gray-700">
      {/* Title bar / drag handle */}
      <div
        ref={handleRef}
        className="h-9 flex items-center gap-2 px-3 bg-gray-100 dark:bg-gray-900 cursor-move select-none shrink-0"
      >
        <FontAwesomeIcon icon={faGripVertical} className="w-3 h-3 text-gray-400 dark:text-gray-500" />
        <span className="text-gray-800 dark:text-white text-xs font-medium truncate flex-1">Stream</span>

        {/* Expand / Collapse */}
        <button
          onClick={handleExpand}
          title={isExpanded ? 'Verkleinern' : 'Vergrößern'}
          className="text-gray-400 hover:text-gray-800 dark:hover:text-white cursor-pointer transition-colors p-1"
        >
          <FontAwesomeIcon icon={isExpanded ? faCompress : faExpand} className="w-3.5 h-3.5" />
        </button>

        {/* Pop-out */}
        <button
          onClick={handlePopOut}
          title="Im neuen Fenster öffnen"
          className="text-gray-400 hover:text-gray-800 dark:hover:text-white cursor-pointer transition-colors p-1"
        >
          <FontAwesomeIcon icon={faExternalLinkAlt} className="w-3.5 h-3.5" />
        </button>

        {/* Minimize */}
        <button
          onClick={onMinimize}
          title="Minimieren"
          className="text-gray-400 hover:text-gray-800 dark:hover:text-white cursor-pointer transition-colors p-1"
        >
          <FontAwesomeIcon icon={faWindowMinimize} className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Video */}
      <div className="flex flex-1 min-h-0">
        <div
          className="relative bg-black"
          style={isExpanded ? { flex: 1, minWidth: 0 } : { width: VIDEO_WIDTH_SMALL, aspectRatio: '16/9' }}
        >
          <div ref={playerTargetRef} className="absolute inset-0 w-full h-full" />
          {isDragging && <div className="absolute inset-0 z-10" />}
        </div>
      </div>
    </div>
  );
}
